import React, { ReactElement } from 'react';
import { ReportEntity, WorkDayUserEntity } from '@data';
import { useTranslation } from 'react-i18next';
import { dayKey, isWeekendDate, useLeavingRows } from '@app/utils';
import { FULL_WORKDAY_MINUTES } from '@app/constants/app';
import { Switch } from '@app/components/core';
import CollapseIn from '@app/components/core/collapse-in';
import SprintDayReport from '@app/components/shared/sprintDayReport';

export interface ToReportListProps {
  workDaysData: WorkDayUserEntity[];
  todayDate: Date;
  /** First day of the window, as `yyyy-mm-dd`. Undefined while the sprints it comes from are loading. */
  windowStart?: string;
  showAddForm: (workDay: string, productIds?: string[]) => void;
  showEditForm: (report: ReportEntity) => void;
  showDeleteForm: (report: ReportEntity) => void;
  showWeekend: boolean;
  toggleShowWeekend: () => void;
  /** Days the member has called done, as `yyyy-mm-dd`. They leave this list for Overview. */
  finishedDays: string[];
  onSetFinished: (workday: string, finished: boolean) => void;
}

/** The days you still owe: weekdays in the window short of a full day, most recent first. */
const ToReportList = ({
  workDaysData,
  todayDate,
  windowStart,
  showAddForm,
  showEditForm,
  showDeleteForm,
  showWeekend,
  toggleShowWeekend,
  finishedDays,
  onSetFinished,
}: ToReportListProps): ReactElement => {
  const { t } = useTranslation();
  const finished = React.useMemo(() => new Set(finishedDays), [finishedDays]);

  // Bounded at both ends whatever the window arithmetic does: a future day cannot be reported, and a day
  // older than the window is no longer a gap you are chasing.
  const today = dayKey(todayDate);
  // A full eight hours, or a mark while time is still on it. The server's rule too, for the count and the dots.
  const isSettled = (day: WorkDayUserEntity): boolean =>
    day.totalMinutes >= FULL_WORKDAY_MINUTES || (finished.has(day.date) && day.totalMinutes > 0);
  const toReport = workDaysData.filter(
    day => !isSettled(day) && (!windowStart || day.date >= windowStart) && day.date <= today,
  );

  // Held one collapse longer than the rule keeps them, whatever settled the day.
  const { rows, isLeaving } = useLeavingRows(toReport, day => day.date);

  // Hidden weekends must not keep the caught-up message away — it answers for what is on screen. A row on
  // its way out is already settled, so it does not hold the message back either.
  const visibleCount = rows.filter(day => !isLeaving(day) && (showWeekend || !isWeekendDate(day.date))).length;

  const dayReport = (item: WorkDayUserEntity): ReactElement => (
    <SprintDayReport
      workDay={item}
      todayDate={todayDate}
      showAddForm={showAddForm}
      showEditForm={showEditForm}
      showDeleteForm={showDeleteForm}
      onSetFinished={isFinished => onSetFinished(item.date, isFinished)}
    />
  );

  return (
    <section className="w-full rounded-sm bg-card p-6 max-md:-mx-4 max-md:w-auto max-md:rounded-none max-md:p-4">
      <div className="mb-1 flex flex-wrap items-start justify-between gap-3">
        {/* No count beside the heading: the Reporting nav item carries the same number, and a number in
            two places is a number that can disagree with itself. */}
        <h2 className="text-base font-bold text-foreground">{t('overview.toReport')}</h2>
        <Switch value={showWeekend} onChange={toggleShowWeekend} text={t('titles.showWeekend')} className="mt-1" />
      </div>
      <p className="mb-6 text-sm text-muted-foreground">{t('overview.toReportHint')}</p>
      {rows.map((item, index) => (
        // Every row is a CollapseIn: the weekend toggle animates both ways through it, and so does a day
        // leaving the list. CollapseIn never transitions on its first render, so the page still paints flat.
        <CollapseIn key={item.date} show={!isLeaving(item) && (showWeekend || !isWeekendDate(item.date))}>
          {/* The gap sits inside the collapsing box, and on the wrapper rather than the row, whose own
              `not-last` rule cannot see its siblings from in here. A hidden row then takes its gap with it. */}
          <div className={index < rows.length - 1 ? 'pb-5' : undefined}>{dayReport(item)}</div>
        </CollapseIn>
      ))}
      {visibleCount === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">{t('overview.allCaughtUp')}</p>
      )}
    </section>
  );
};

export default ToReportList;
